import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn as AssignmentDoneIcon,
  CheckCircle as CheckCircleIcon,
  EmojiEvents as TrophyIcon,
  LocalFireDepartment as StreakIcon,
  MenuBook as NotesIcon,
  PlayCircleFilled as VideoIcon,
  QrCode2 as QrCodeIcon,
  Quiz as QuizIcon,
  RestartAlt as RestartAltIcon,
  RocketLaunch as BoostIcon,
  Speed as SpeedIcon,
  Style as FlashCardIcon,
} from '@mui/icons-material';

export const CERTIFICATE_ISSUER = 'KNLS Computers';
export const CERTIFICATE_PRIMARY = '#1E3A78';

const MILESTONES = [
  { label: 'Start', value: 0 },
  { label: 'Bronze', value: 25 },
  { label: 'Silver', value: 50 },
  { label: 'Typing Pro', value: 70 },
  { label: 'Gold', value: 85 },
  { label: 'Certified', value: 100 },
];

export const clampProgress = (value, min = 0, max = 100) =>
  Math.min(max, Math.max(min, Number.isFinite(Number(value)) ? Number(value) : 0));

export const toTitleCase = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const getCertificateId = (student) => {
  const rawId = student?.student_id || student?.id || Date.now();
  return `KNLS-CERT-${String(rawId).replace(/[^a-zA-Z0-9]/g, '').slice(-12).toUpperCase()}`;
};

export const resolveStudentAvatar = (student, apiOrigin = '') => {
  const photoUrl =
    student?.photo_url ||
    student?.admission_photo_url ||
    student?.photo ||
    student?.avatar ||
    '';
  if (!photoUrl) return undefined;
  if (/^(data:image\/|blob:|https?:\/\/)/i.test(photoUrl)) {
    return photoUrl.startsWith('data:')
      ? photoUrl
      : `${photoUrl}${photoUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
  }
  const origin = apiOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
  const absolute = photoUrl.startsWith('/') ? `${origin}${photoUrl}` : `${origin}/${photoUrl}`;
  return `${absolute}${absolute.includes('?') ? '&' : '?'}t=${Date.now()}`;
};

export const formatCourseNames = (courses, courseMap = {}) => {
  if (!courses || !Array.isArray(courses)) return 'N/A';
  return courses.map((c) => courseMap[c] || c).join(', ') || 'N/A';
};

export function buildLiveCertificateData(student, progressData = null) {
  if (progressData?.success !== false && progressData) {
    const metrics = [
      {
        label: 'Videos Watched',
        value: progressData.metrics?.videos ?? 0,
        icon: <VideoIcon />,
        color: '#6D28D9',
        detail: progressData.metricCounts?.video
          ? `${progressData.metricCounts.video.completed}/${progressData.metricCounts.video.total} items`
          : null,
      },
      {
        label: 'Flash Cards',
        value: progressData.metrics?.flashcards ?? 0,
        icon: <FlashCardIcon />,
        color: '#10B981',
        detail: progressData.metricCounts?.flashcard
          ? `${progressData.metricCounts.flashcard.completed}/${progressData.metricCounts.flashcard.total} items`
          : null,
      },
      {
        label: 'Notes Read',
        value: progressData.metrics?.notes ?? 0,
        icon: <NotesIcon />,
        color: '#2563EB',
        detail: progressData.metricCounts?.notes
          ? `${progressData.metricCounts.notes.completed}/${progressData.metricCounts.notes.total} items`
          : null,
      },
      {
        label: 'Quizzes',
        value: progressData.metrics?.quizzes ?? 0,
        icon: <QuizIcon />,
        color: '#F97316',
        detail: progressData.metricCounts?.quiz
          ? `${progressData.metricCounts.quiz.completed}/${progressData.metricCounts.quiz.total} items`
          : null,
      },
      {
        label: 'Assignments',
        value: progressData.metrics?.assignments ?? 0,
        icon: <AssignmentDoneIcon />,
        color: '#0EA5E9',
        detail: progressData.metricCounts?.assignment
          ? `${progressData.metricCounts.assignment.completed}/${progressData.metricCounts.assignment.total} items`
          : null,
      },
      {
        label: 'TT Speed',
        value: clampProgress((progressData.ttSpeed / 50) * 100, 0, 100),
        icon: <SpeedIcon />,
        color: '#7C3AED',
        detail: progressData.ttSpeed
          ? `${progressData.ttSpeed} WPM | ${progressData.ttAccuracy}% Accuracy`
          : 'No typing record yet',
      },
      {
        label: 'Daily Streak',
        value: clampProgress((progressData.dailyStreak / 7) * 100, 0, 100),
        icon: <StreakIcon />,
        color: '#EF4444',
        detail: `${progressData.dailyStreak || 0} day${progressData.dailyStreak === 1 ? '' : 's'}`,
      },
    ];

    const incompleteVideos = Math.max(
      0,
      (progressData.metricCounts?.video?.total || 0) -
        (progressData.metricCounts?.video?.completed || 0),
    );
    const incompleteQuizzes = Math.max(
      0,
      (progressData.metricCounts?.quiz?.total || 0) -
        (progressData.metricCounts?.quiz?.completed || 0),
    );
    const incompleteAssignments = Math.max(
      0,
      (progressData.metricCounts?.assignment?.total || 0) -
        (progressData.metricCounts?.assignment?.completed || 0),
    );

    return {
      overall: progressData.overall ?? 0,
      medal: progressData.medal ?? { label: 'Bronze Learner', color: '#B45309', threshold: '30%' },
      ttSpeed: progressData.ttSpeed ?? 0,
      ttAccuracy: progressData.ttAccuracy ?? 0,
      nextTarget: progressData.nextTarget ?? 'Gold Certificate',
      hasAppActivity: progressData.hasAppActivity === true,
      metrics,
      courses: progressData.courses || [],
      totals: progressData.totals || { chapters: 0, completedChapters: 0 },
      enrolledToday: progressData.student?.enrolledToday,
      dataSource: progressData.dataSource || 'lms_student_progress',
      tasks: [
        incompleteVideos > 0
          ? {
              label: `Watch ${Math.min(2, incompleteVideos)} more video${incompleteVideos === 1 ? '' : 's'}`,
              boost: '+6%',
              icon: <VideoIcon />,
            }
          : null,
        incompleteQuizzes > 0
          ? {
              label: `Complete ${Math.min(1, incompleteQuizzes)} quiz`,
              boost: '+8%',
              icon: <QuizIcon />,
            }
          : null,
        progressData.ttSpeed
          ? {
              label: `Improve TT Speed to ${Math.max(45, progressData.ttSpeed + 3)} WPM`,
              boost: '+7%',
              icon: <SpeedIcon />,
            }
          : { label: 'Record TT Speed in attendance', boost: '+7%', icon: <SpeedIcon /> },
        incompleteAssignments > 0
          ? { label: 'Submit assignment', boost: '+10%', icon: <AssignmentDoneIcon /> }
          : null,
      ].filter(Boolean),
    };
  }

  const status = String(student?.status || '').toLowerCase();
  const isCompleted = status.includes('completed') || Boolean(student?.certificate_issued_at);
  return {
    overall: isCompleted ? 100 : 0,
    medal: isCompleted
      ? { label: 'Certified', color: '#1E3A78', threshold: '100%' }
      : { label: 'Bronze Learner', color: '#B45309', threshold: '30%' },
    ttSpeed: 0,
    ttAccuracy: 0,
    nextTarget: 'Gold Certificate',
    metrics: [
      { label: 'Videos Watched', value: 0, icon: <VideoIcon />, color: '#6D28D9', detail: '0/0 items' },
      { label: 'Flash Cards', value: 0, icon: <FlashCardIcon />, color: '#10B981', detail: '0/0 items' },
      { label: 'Notes Read', value: 0, icon: <NotesIcon />, color: '#2563EB', detail: '0/0 items' },
      { label: 'Quizzes', value: 0, icon: <QuizIcon />, color: '#F97316', detail: '0/0 items' },
      { label: 'Assignments', value: 0, icon: <AssignmentDoneIcon />, color: '#0EA5E9', detail: '0/0 items' },
      { label: 'TT Speed', value: 0, icon: <SpeedIcon />, color: '#7C3AED', detail: 'No typing record yet' },
      { label: 'Daily Streak', value: 0, icon: <StreakIcon />, color: '#EF4444', detail: '0 days' },
    ],
    courses: [],
    totals: { chapters: 0, completedChapters: 0 },
    enrolledToday: false,
    hasAppActivity: false,
    dataSource: 'lms_student_progress',
    tasks: [
      { label: 'Open course and watch first lesson', boost: '+6%', icon: <VideoIcon /> },
      { label: 'Complete first quiz', boost: '+8%', icon: <QuizIcon /> },
      { label: 'Record TT Speed in attendance', boost: '+7%', icon: <SpeedIcon /> },
    ],
  };
}

/**
 * Same Live Digital Certificate UI used in admin (student-scoped: own data only).
 */
export default function LiveDigitalCertificateView({
  student,
  progressData = null,
  loading = false,
  error = '',
  courseTab = 0,
  onCourseTabChange,
  onRefresh,
  courseMap = {},
  certificateNavArrows = null,
}) {
  const liveData = buildLiveCertificateData(student, loading ? null : progressData);
  const studentName = toTitleCase(student?.name || 'Student');
  const courseName = formatCourseNames(student?.enrolled_courses, courseMap);
  const avatar = resolveStudentAvatar(student);
  const activeCourse = liveData.courses?.[courseTab] || liveData.courses?.[0] || null;

  return (
    <Box>
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading real-time LMS progress...
          </Typography>
        </Box>
      )}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}. Showing zero progress until data is available.
        </Alert>
      )}
      {!loading && !error && liveData.hasAppActivity === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No LMS app activity yet. Progress stays at 0% until you open courses and start lessons.
        </Alert>
      )}
      {liveData.enrolledToday && liveData.hasAppActivity && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Enrolled today. Progress below is live from LMS activity only.
        </Alert>
      )}
      <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
        Real stats: {liveData.totals.completedChapters}/{liveData.totals.chapters} chapters completed
        across enrolled courses. Source: {liveData.dataSource}.
        {progressData?.generatedAt
          ? ` Updated: ${new Date(progressData.generatedAt).toLocaleString()}`
          : ''}
      </Alert>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 16px 40px rgba(15,23,42,0.08)' }}>
            <CardContent>
              <Typography variant="overline" color="secondary" sx={{ fontWeight: 900 }}>
                Student Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
                <Avatar src={avatar} sx={{ width: 62, height: 62, border: '3px solid #7C3AED' }}>
                  {!avatar && studentName.charAt(0)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }} noWrap>
                    {studentName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {courseName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {student?.student_id || student?.id}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15 }}>
                {liveData.metrics.map((metric) => (
                  <Box
                    key={metric.label}
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      border: '1px solid #E5E7EB',
                      bgcolor: '#fff',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.7 }}>
                      <Box sx={{ color: metric.color, display: 'flex' }}>{metric.icon}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                          {metric.label}
                        </Typography>
                        {metric.detail && (
                          <Typography variant="caption" color="text.secondary">
                            {metric.detail}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 900, color: metric.color }}>
                        {Math.round(metric.value)}%
                      </Typography>
                    </Box>
                    <Box sx={{ height: 6, borderRadius: 999, bgcolor: '#EEF2FF', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${Math.round(metric.value)}%`,
                          height: '100%',
                          borderRadius: 999,
                          bgcolor: metric.color,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative' }}>
            {certificateNavArrows}
            <Box
              sx={{
                position: 'relative',
                minHeight: { xs: 430, md: 560 },
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                overflow: 'hidden',
                border: `6px solid ${CERTIFICATE_PRIMARY}`,
                boxShadow:
                  'inset 0 0 0 4px rgba(201,162,74,0.8), 0 20px 50px rgba(30,58,120,0.16)',
                background:
                  'radial-gradient(circle at 20% 18%, rgba(124,58,237,0.12), transparent 30%), linear-gradient(135deg, #FFFBEB, #FFFFFF 48%, #EEF2FF)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 18,
                  border: '2px solid rgba(201,162,74,0.75)',
                  pointerEvents: 'none',
                }}
              />
              <Typography
                sx={{
                  position: 'absolute',
                  top: '42%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) rotate(-12deg)',
                  fontSize: { xs: 76, md: 116 },
                  fontWeight: 900,
                  color: 'rgba(124,58,237,0.08)',
                  letterSpacing: 5,
                  pointerEvents: 'none',
                }}
              >
                {liveData.overall}%
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Chip
                  icon={<BoostIcon />}
                  label="Live Digital Certificate"
                  sx={{ alignSelf: 'center', bgcolor: '#F59E0B', color: '#fff', fontWeight: 900, mb: 2 }}
                />
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 900, color: CERTIFICATE_PRIMARY, letterSpacing: 1 }}
                >
                  {CERTIFICATE_ISSUER}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#111827', my: 1 }}
                >
                  Certificate
                </Typography>
                <Typography variant="h6" sx={{ color: '#92400E', fontWeight: 800 }}>
                  of Learning Progress
                </Typography>
                <Avatar src={avatar} sx={{ width: 78, height: 78, my: 2, border: '4px solid #FBBF24' }}>
                  {!avatar && studentName.charAt(0)}
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{ fontFamily: 'Georgia, serif', color: '#581C87', fontWeight: 800 }}
                >
                  {studentName}
                </Typography>
                <Typography sx={{ maxWidth: 560, mt: 1, color: 'text.secondary' }}>
                  has completed {liveData.totals.completedChapters} of {liveData.totals.chapters}{' '}
                  published chapters across enrolled courses ({liveData.overall}% content progress from
                  live LMS tracking).
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.2,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    mt: 2,
                  }}
                >
                  <Chip
                    icon={<TrophyIcon />}
                    label={liveData.medal.label}
                    sx={{ bgcolor: liveData.medal.color, color: '#fff', fontWeight: 900 }}
                  />
                  <Chip
                    icon={<SpeedIcon />}
                    label={
                      liveData.ttSpeed
                        ? `TT Speed: ${liveData.ttSpeed} WPM`
                        : 'TT Speed: Not recorded'
                    }
                    sx={{ bgcolor: '#EEF2FF', fontWeight: 800 }}
                  />
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={
                      liveData.ttAccuracy
                        ? `${liveData.ttAccuracy}% Accuracy`
                        : 'Accuracy: Not recorded'
                    }
                    sx={{ bgcolor: '#ECFDF5', fontWeight: 800 }}
                  />
                </Box>
                <Box
                  sx={{
                    mt: 'auto',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    width: '100%',
                    gap: 2,
                  }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="caption" color="text.secondary">
                      Certificate ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {getCertificateId(student)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 82,
                      height: 82,
                      border: '1px solid #CBD5E1',
                      borderRadius: 2,
                      bgcolor: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <QrCodeIcon sx={{ fontSize: 54, color: CERTIFICATE_PRIMARY }} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 16px 40px rgba(15,23,42,0.08)' }}>
            <CardContent>
              <Typography variant="overline" color="secondary" sx={{ fontWeight: 900 }}>
                Certificate Upgrade
              </Typography>
              <Box sx={{ position: 'relative', width: 154, height: 154, mx: 'auto', my: 2 }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={154}
                  thickness={5}
                  sx={{ color: '#EDE9FE', position: 'absolute' }}
                />
                <CircularProgress
                  variant="determinate"
                  value={liveData.overall}
                  size={154}
                  thickness={5}
                  sx={{ color: '#7C3AED' }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#4C1D95' }}>
                      {liveData.overall}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ready
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, textAlign: 'center' }}>
                    <TrophyIcon sx={{ color: liveData.medal.color }} />
                    <Typography variant="caption" display="block" color="text.secondary">
                      Current Rank
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {liveData.medal.label}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, textAlign: 'center' }}>
                    <TrophyIcon sx={{ color: '#D97706' }} />
                    <Typography variant="caption" display="block" color="text.secondary">
                      Next Rank
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {liveData.nextTarget}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mt: 2, mb: 1 }}>
                Today&apos;s Boost Tasks
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {liveData.tasks.map((task) => (
                  <Paper
                    key={task.label}
                    variant="outlined"
                    sx={{ p: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Box sx={{ color: '#7C3AED', display: 'flex' }}>{task.icon}</Box>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 700 }}>
                      {task.label}
                    </Typography>
                    <Chip
                      label={task.boost}
                      size="small"
                      sx={{ bgcolor: '#6D28D9', color: '#fff', fontWeight: 900 }}
                    />
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #DCFCE7' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Course Chapter Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Green = chapter fully completed. Red = not completed yet. Counts use published LMS
                  chapters only.
                </Typography>
              </Box>
              {onRefresh && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  disabled={loading}
                  onClick={onRefresh}
                >
                  Refresh
                </Button>
              )}
            </Box>

            {liveData.courses?.length > 0 ? (
              <>
                <Tabs
                  value={courseTab}
                  onChange={(_, value) => onCourseTabChange?.(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  {liveData.courses.map((course, index) => (
                    <Tab
                      key={course.courseCode}
                      value={index}
                      label={`${course.courseName} (${course.completedChapters}/${course.totalChapters})`}
                    />
                  ))}
                </Tabs>

                {activeCourse && (
                  <Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                      <Chip
                        size="small"
                        color={activeCourse.hasAccessed ? 'success' : 'default'}
                        label={activeCourse.hasAccessed ? 'Course accessed' : 'Not opened yet'}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${activeCourse.totalChapters} total chapters`}
                      />
                      <Chip
                        size="small"
                        sx={{ bgcolor: '#DCFCE7', color: '#166534', fontWeight: 700 }}
                        label={`${activeCourse.completedChapters} completed`}
                      />
                      <Chip
                        size="small"
                        sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 700 }}
                        label={`${Math.max(
                          0,
                          activeCourse.totalChapters - activeCourse.completedChapters,
                        )} pending`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${activeCourse.chapterProgressPercent}% chapter progress`}
                      />
                    </Stack>

                    <Box
                      sx={{
                        maxHeight: 320,
                        overflowY: 'auto',
                        display: 'grid',
                        gap: 1,
                        pr: 0.5,
                      }}
                    >
                      {activeCourse.chapters?.length > 0 ? (
                        activeCourse.chapters.map((chapter) => {
                          const isCompleted = chapter.isCompleted;
                          const statusColor = isCompleted ? '#16A34A' : '#DC2626';
                          const statusBg = isCompleted ? '#DCFCE7' : '#FEE2E2';
                          return (
                            <Box
                              key={chapter.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.25,
                                p: 1.25,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: isCompleted ? '#BBF7D0' : '#FECACA',
                                bgcolor: statusBg,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: statusColor,
                                  mt: 0.6,
                                  flexShrink: 0,
                                  boxShadow: `0 0 0 3px ${
                                    isCompleted
                                      ? 'rgba(22,163,74,0.18)'
                                      : 'rgba(220,38,38,0.18)'
                                  }`,
                                }}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                  {chapter.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {chapter.subjectName} · {chapter.completedItems}/
                                  {chapter.totalItems} items ·{' '}
                                  {isCompleted
                                    ? 'Watched / completed'
                                    : chapter.hasStarted
                                      ? 'Started, not finished'
                                      : 'Not watched yet'}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={isCompleted ? 'Done' : 'Pending'}
                                sx={{
                                  bgcolor: statusColor,
                                  color: '#fff',
                                  fontWeight: 800,
                                }}
                              />
                            </Box>
                          );
                        })
                      ) : (
                        <Alert severity="info">No published chapters found in this course yet.</Alert>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">
                No enrolled course progress available yet. Open your LMS lessons to start tracking.
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #EDE9FE' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 2 }}>
              Certificate Journey
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                gap: 1.5,
              }}
            >
              {MILESTONES.map((item) => {
                const unlocked = liveData.overall >= item.value;
                return (
                  <Box key={item.label} sx={{ textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        mx: 'auto',
                        mb: 0.75,
                        bgcolor: unlocked ? '#7C3AED' : '#F1F5F9',
                        color: unlocked ? '#fff' : '#64748B',
                        border: item.label === 'Gold' ? '2px solid #F59E0B' : 'none',
                      }}
                    >
                      {unlocked ? <CheckCircleIcon /> : <TrophyIcon />}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.value}%
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
